import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/core/actions/dropdown-menu";
import Badge from "@/components/core/badge";
import { Button } from "@/components/core/buttons/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/core/card";
import { exportToCSV, exportToPDF, exportToXLSX } from "@/utils/export-data";
import {
	IconCalendarDue,
	IconCalendarOff,
	IconChevronDown,
	IconChevronRight,
	IconCurrencyPeso,
	IconFileExport,
	IconFileSpreadsheet,
	IconFileTypeCsv,
	IconFileTypePdf,
} from "@tabler/icons-react";
import { addDays, differenceInDays, format } from "date-fns";
import type React from "react";
import { useState } from "react";

interface Invoice {
	id: string;
	invoice_number: string;
	amount: number;
	due_date: string;
	status: "pending" | "paid" | "overdue" | "cancelled";
	collection_status: "pending" | "collected";
	sales_contract: {
		id: string;
		salesperson?: {
			id: string;
			name: string;
			email: string;
		};
		project?: {
			id: string;
			name: string;
			client?: {
				id: string;
				name: string;
				company?: string;
			};
		};
	};
}

interface UpcomingInvoiceDueDatesProps {
	invoices: Invoice[];
	className?: string;
}

export const UpcomingInvoiceDueDates: React.FC<UpcomingInvoiceDueDatesProps> = ({ invoices, className }) => {
	const today = new Date();

	// Helper function to group invoices by project
	const groupByProject = (invoiceList: Invoice[]) => {
		const grouped: { [key: string]: { projectName: string; clientName: string; invoices: Invoice[] } } = {};

		for (const invoice of invoiceList) {
			const projectId = invoice.sales_contract.project?.id || "no-project";
			const projectName = invoice.sales_contract.project?.name || "No Project";
			const clientName = invoice.sales_contract.project?.client?.company || invoice.sales_contract.project?.client?.name || "No Client";

			if (!grouped[projectId]) {
				grouped[projectId] = {
					projectName,
					clientName,
					invoices: [],
				};
			}
			grouped[projectId].invoices.push(invoice);
		}

		// Sort invoices within each project by due date
		for (const group of Object.values(grouped)) {
			group.invoices.sort((a, b) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime());
		}

		return grouped;
	};

	// Group invoices by time periods
	const groupedInvoices = {
		overdue: invoices.filter((invoice) => {
			const dueDate = new Date(invoice.due_date);
			return differenceInDays(dueDate, today) < 0; // Past due date
		}),
		critical: invoices.filter((invoice) => {
			const dueDate = new Date(invoice.due_date);
			return differenceInDays(dueDate, today) <= 7 && differenceInDays(dueDate, today) >= 0;
		}),
		thisMonth: invoices.filter((invoice) => {
			const dueDate = new Date(invoice.due_date);
			const days = differenceInDays(dueDate, today);
			return days > 7 && days <= 30;
		}),
		nextMonth: invoices.filter((invoice) => {
			const dueDate = new Date(invoice.due_date);
			const days = differenceInDays(dueDate, today);
			return days > 30 && days <= 60;
		}),
		threeMonths: invoices.filter((invoice) => {
			const dueDate = new Date(invoice.due_date);
			const days = differenceInDays(dueDate, today);
			return days > 60 && days <= 90;
		}),
	};

	const formatCurrency = (value: number) => {
		const numValue = typeof value === "number" && !Number.isNaN(value) ? value : 0;
		return new Intl.NumberFormat("en-PH", {
			style: "currency",
			currency: "PHP",
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(numValue);
	};

	const handleExport = async (exportFormat: "csv" | "xlsx" | "pdf") => {
		const headers = ["Project", "Client", "Invoice #", "Due Date", "Days", "Amount"];
		const rows = invoices
			.sort((a, b) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime())
			.map((inv) => {
				const daysLeft = differenceInDays(new Date(inv.due_date), today);
				const amount = typeof inv.amount === "number" ? inv.amount : Number.parseFloat(String(inv.amount)) || 0;
				return [
					inv.sales_contract.project?.name || "N/A",
					inv.sales_contract.project?.client?.company || inv.sales_contract.project?.client?.name || "N/A",
					inv.invoice_number,
					format(new Date(inv.due_date), "yyyy-MM-dd"),
					daysLeft < 0 ? `${Math.abs(daysLeft)}d overdue` : `${daysLeft}d`,
					amount,
				];
			});

		const filename = `pending-invoice-collections-${format(today, "yyyy-MM-dd")}`;

		if (exportFormat === "csv") {
			exportToCSV({ headers, rows, filename });
		} else if (exportFormat === "xlsx") {
			exportToXLSX({ headers, rows, filename });
		} else {
			exportToPDF({ headers, rows, filename });
		}
	};

	const InvoiceItem = ({ invoice, showProject = true }: { invoice: Invoice; showProject?: boolean }) => {
		const daysLeft = differenceInDays(new Date(invoice.due_date), today);
		const isOverdue = daysLeft < 0;

		return (
			<div className="flex items-center justify-between py-1.5 px-2 hover:bg-gray-50 rounded transition-colors">
				<div className="flex-1">
					<div className="flex items-center gap-2">
						<span className="font-medium text-xs">{invoice.invoice_number}</span>
						<Badge variant={isOverdue ? "destructive" : daysLeft <= 7 ? "destructive" : daysLeft <= 30 ? "warning" : "default"} className="text-xs">
							{isOverdue ? `${Math.abs(daysLeft)}d overdue` : `${daysLeft}d`}
						</Badge>
					</div>
					{showProject && (
						<div className="text-xs text-gray-500 mt-0.5">
							{invoice.sales_contract.project?.client?.company || invoice.sales_contract.project?.client?.name || "No client"} •
							{invoice.sales_contract.project?.name || "N/A"} •{" "}
							{formatCurrency(typeof invoice.amount === "number" ? invoice.amount : Number.parseFloat(String(invoice.amount)) || 0)}
						</div>
					)}
				</div>
				<div className="flex flex-col items-end gap-1">
					<div className="text-xs font-medium">
						{formatCurrency(typeof invoice.amount === "number" ? invoice.amount : Number.parseFloat(String(invoice.amount)) || 0)}
					</div>
					<div className="text-xs text-gray-500">{format(new Date(invoice.due_date), "MMM d")}</div>
				</div>
			</div>
		);
	};

	const ProjectGroup = ({
		projectName,
		clientName,
		invoices: projectInvoices,
		projectId,
	}: { projectName: string; clientName: string; invoices: Invoice[]; projectId: string }) => {
		const [isExpanded, setIsExpanded] = useState(false);
		const totalAmount = projectInvoices.reduce((sum, inv) => {
			const amount = typeof inv.amount === "number" ? inv.amount : Number.parseFloat(String(inv.amount)) || 0;
			return sum + amount;
		}, 0);

		return (
			<div className="border-l-2 border-blue-200 pl-3 mb-3">
				<button
					type="button"
					className="flex items-center justify-between mb-1 cursor-pointer hover:bg-gray-50 rounded p-1 -mx-1 transition-colors w-full text-left"
					onClick={() => setIsExpanded(!isExpanded)}
				>
					<div className="flex items-center gap-2 flex-1">
						{isExpanded ? (
							<IconChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
						) : (
							<IconChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
						)}
						<div className="flex-1">
							<div className="font-medium text-sm text-gray-900">{projectName}</div>
							<div className="text-xs text-gray-500">{clientName}</div>
						</div>
					</div>
					<div className="text-right flex-shrink-0">
						<div className="text-sm font-medium">{formatCurrency(totalAmount)}</div>
						<div className="text-xs text-gray-500">
							{projectInvoices.length} invoice{projectInvoices.length > 1 ? "s" : ""}
						</div>
					</div>
				</button>
				{isExpanded && (
					<div className="space-y-0.5 mt-2 ml-6">
						{projectInvoices.map((invoice) => (
							<InvoiceItem key={invoice.id} invoice={invoice} showProject={false} />
						))}
					</div>
				)}
			</div>
		);
	};

	return (
		<Card className={className}>
			<CardHeader>
				<div className="flex items-center justify-between">
					<CardTitle className="flex items-center gap-2">
						<IconCalendarDue className="h-5 w-5" />
						Pending Invoice Collections
					</CardTitle>
					{invoices.length > 0 && (
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button variant="outline" size="sm">
									<IconFileExport className="w-3 h-3 mr-1" />
									Export
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								<DropdownMenuItem onClick={() => handleExport("csv")}>
									<IconFileTypeCsv className="w-4 h-4 mr-2" />
									Export as CSV
								</DropdownMenuItem>
								<DropdownMenuItem onClick={() => handleExport("xlsx")}>
									<IconFileSpreadsheet className="w-4 h-4 mr-2" />
									Export as Excel
								</DropdownMenuItem>
								<DropdownMenuItem onClick={() => handleExport("pdf")}>
									<IconFileTypePdf className="w-4 h-4 mr-2" />
									Export as PDF
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					)}
				</div>
			</CardHeader>
			<CardContent className="max-h-[600px] overflow-y-auto">
				<div className="space-y-4 pr-2">
					{/* Overdue - Past due date */}
					{groupedInvoices.overdue.length > 0 && (
						<div>
							<div className="flex items-center gap-2 mb-2 sticky top-0 bg-white py-1 z-10">
								<IconCalendarOff className="h-4 w-4 text-red-600" />
								<h4 className="text-sm font-semibold text-red-700">🚨 OVERDUE</h4>
								<Badge variant="destructive" className="text-xs bg-red-700">
									{groupedInvoices.overdue.length}
								</Badge>
							</div>
							<div className="space-y-2 ml-6">
								{Object.entries(groupByProject(groupedInvoices.overdue)).map(([projectId, group]) => (
									<ProjectGroup key={projectId} projectId={projectId} projectName={group.projectName} clientName={group.clientName} invoices={group.invoices} />
								))}
							</div>
						</div>
					)}

					{/* Critical - Within 7 days */}
					{groupedInvoices.critical.length > 0 && (
						<div>
							<div className="flex items-center gap-2 mb-2 sticky top-0 bg-white py-1 z-10">
								<IconCalendarOff className="h-4 w-4 text-red-500" />
								<h4 className="text-sm font-semibold text-red-600">Critical - Due This Week</h4>
								<Badge variant="destructive" className="text-xs">
									{groupedInvoices.critical.length}
								</Badge>
							</div>
							<div className="space-y-2 ml-6">
								{Object.entries(groupByProject(groupedInvoices.critical)).map(([projectId, group]) => (
									<ProjectGroup key={projectId} projectId={projectId} projectName={group.projectName} clientName={group.clientName} invoices={group.invoices} />
								))}
							</div>
						</div>
					)}

					{/* This Month - 8-30 days */}
					{groupedInvoices.thisMonth.length > 0 && (
						<div>
							<div className="flex items-center gap-2 mb-2 sticky top-0 bg-white py-1 z-10">
								<h4 className="text-sm font-semibold text-orange-600">This Month</h4>
								<Badge variant="warning" className="text-xs">
									{groupedInvoices.thisMonth.length}
								</Badge>
							</div>
							<div className="space-y-2 ml-6">
								{Object.entries(groupByProject(groupedInvoices.thisMonth)).map(([projectId, group]) => (
									<ProjectGroup key={projectId} projectId={projectId} projectName={group.projectName} clientName={group.clientName} invoices={group.invoices} />
								))}
							</div>
						</div>
					)}

					{/* Next Month - 31-60 days */}
					{groupedInvoices.nextMonth.length > 0 && (
						<div>
							<div className="flex items-center gap-2 mb-2 sticky top-0 bg-white py-1 z-10">
								<h4 className="text-sm font-semibold text-yellow-600">Next Month</h4>
								<Badge variant="secondary" className="text-xs">
									{groupedInvoices.nextMonth.length}
								</Badge>
							</div>
							<div className="space-y-2 ml-6">
								{Object.entries(groupByProject(groupedInvoices.nextMonth)).map(([projectId, group]) => (
									<ProjectGroup key={projectId} projectId={projectId} projectName={group.projectName} clientName={group.clientName} invoices={group.invoices} />
								))}
							</div>
						</div>
					)}

					{/* 3 Months - 61-90 days */}
					{groupedInvoices.threeMonths.length > 0 && (
						<div>
							<div className="flex items-center gap-2 mb-2 sticky top-0 bg-white py-1 z-10">
								<h4 className="text-sm font-semibold text-gray-600">Within 3 Months</h4>
								<Badge variant="outline" className="text-xs">
									{groupedInvoices.threeMonths.length}
								</Badge>
							</div>
							<div className="space-y-2 ml-6">
								{Object.entries(groupByProject(groupedInvoices.threeMonths)).map(([projectId, group]) => (
									<ProjectGroup key={projectId} projectId={projectId} projectName={group.projectName} clientName={group.clientName} invoices={group.invoices} />
								))}
							</div>
						</div>
					)}

					{/* No pending collections */}
					{invoices.length === 0 && (
						<div className="text-center py-4">
							<p className="text-sm text-gray-500">No pending invoice collections requiring attention</p>
						</div>
					)}
				</div>
			</CardContent>
		</Card>
	);
};
